import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { Serialiser } from "hooks/serialisers/types";
import { useTenant } from "hooks/settings";
import { useMaterials } from "hooks/useMaterials";
import { useQueryParamState } from "hooks/useQueryParamState";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DeliveryAdjustmentMaterialItem,
  useCreateDeliveryAdjustmentMutation,
  useGetLatestDeliveryAdjustmentQuery,
} from "src/store/api/generatedApi";
import { DeliveryAdjustmentsGrid } from "./DeliveryAdjustmentsGrid";
import { DeliveryAdjustmentsToolbar } from "./DeliveryAdjustmentsToolbar";
import type { PasteUpdate } from "./types";

const dayjsSerailiser: Serialiser<Dayjs, never> = {
  format: (value) => value.format("YYYY-MM-DD"),
  parse: (text) => ({ valid: true, value: dayjs(text) }),
};

export const DeliveryAdjustmentsPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const [selectedMonth, setSelectedMonth] = useQueryParamState<Dayjs, never>(
    "month",
    dayjsSerailiser,
    dayjs()
  );

  const [createDeliveryAdjustment] = useCreateDeliveryAdjustmentMutation();
  const { data: latestDeliveryAdjustment, error } =
    useGetLatestDeliveryAdjustmentQuery({
      tenantName,
      month: selectedMonth.format("YYYY-MM-DD"),
    });
  const currentMonthAdjustment = latestDeliveryAdjustment?.items;
  const [rows, setRows] = useState<DeliveryAdjustmentMaterialItem[]>([]);

  useEffect(() => {
    if (currentMonthAdjustment) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- syncs editable rows from API; user modifies locally
      setRows(currentMonthAdjustment);
    } else {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- clears editable rows when no API data
      setRows([]);
    }
  }, [currentMonthAdjustment]);

  const { materials, isLoading: materialsLoading } = useMaterials();
  const availableMaterialNames = useMemo(() => {
    const all = materials?.map((m) => m.name) ?? [];
    const existing = new Set(rows.map((r) => r.material_name));
    return all.filter((name) => !existing.has(name));
  }, [materials, rows]);
  const [newMaterialName, setNewMaterialName] = useState<string | null>(null);
  const [newQuantity, setNewQuantity] = useState<number | null>(null);

  const hasRows = rows.length > 0;
  const hasChanges = useMemo(() => {
    const base = currentMonthAdjustment ?? [];
    if (rows.length !== base.length) return true;
    const baseMap = new Map(
      base.map((item) => [item.material_name, item.quantity] as const)
    );
    for (const row of rows) {
      if (baseMap.get(row.material_name) !== row.quantity) return true;
    }
    return false;
  }, [rows, currentMonthAdjustment]);

  const handleReset = useCallback(() => {
    setRows(currentMonthAdjustment ?? []);
  }, [currentMonthAdjustment]);

  const handleSave = useCallback(async () => {
    await createDeliveryAdjustment({
      tenantName,
      deliveryAdjustmentCreate: {
        month: selectedMonth.format("YYYY-MM-DD"),
        items: rows.map((r) => ({
          material_name: r.material_name,
          quantity: r.quantity,
        })),
      },
    });
  }, [createDeliveryAdjustment, tenantName, selectedMonth, rows]);

  const handleAddRow = useCallback(() => {
    if (!newMaterialName || newQuantity === null) return;
    setRows((prev) => {
      if (prev.some((r) => r.material_name === newMaterialName)) {
        return prev;
      }
      return [
        ...prev,
        {
          material_name: newMaterialName,
          quantity: newQuantity,
          fraction: 0,
        },
      ];
    });
    setNewMaterialName(null);
    setNewQuantity(null);
  }, [newMaterialName, newQuantity]);

  const handleApplyPastedUpdates = useCallback((updates: PasteUpdate[]) => {
    setRows((prev) => {
      const existingNames = new Set(prev.map((r) => r.material_name));
      const updatedExisting = prev.map((row) => {
        const match = updates.find(
          (u) => u.material_name === row.material_name
        );
        return match ? { ...row, quantity: match.quantity } : row;
      });

      const additions: DeliveryAdjustmentMaterialItem[] = updates
        .filter((u) => !existingNames.has(u.material_name))
        .map((u) => ({
          material_name: u.material_name,
          quantity: u.quantity,
          fraction: 0,
        }));

      return [...updatedExisting, ...additions];
    });
  }, []);

  const onProcessRowUpdate = useCallback(
    (newRow: DeliveryAdjustmentMaterialItem) =>
      new Promise<DeliveryAdjustmentMaterialItem>((resolve) => {
        setRows((prev) => {
          const newRows = prev.map((row) =>
            row.material_name === newRow.material_name ? newRow : row
          );
          resolve(newRow);
          return newRows;
        });
      }),
    []
  );

  return (
    <AccountPageLayout title={t("deliveryAdjustments")}>
      <Box
        sx={{
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
        }}
      >
        <DeliveryAdjustmentsToolbar
          selectedMonth={selectedMonth}
          setSelectedMonth={(d) => {
            setRows([]);
            setSelectedMonth(d);
          }}
          hasRows={hasRows}
          hasChanges={hasChanges}
          onSave={handleSave}
          onReset={handleReset}
          onApplyPastedUpdates={handleApplyPastedUpdates}
        >
          <Stack direction="row" spacing={1}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 240 }}
              options={availableMaterialNames}
              loading={materialsLoading}
              value={newMaterialName}
              onChange={(_e, value) => {
                setNewMaterialName(value);
              }}
              renderInput={(params) => (
                <TextField {...params} label={t("material")} />
              )}
            />
            <TextField
              size="small"
              type="number"
              label={t("adjustment")}
              value={newQuantity ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  setNewQuantity(null);
                  return;
                }
                const parsed = parseFloat(val);
                if (!Number.isNaN(parsed)) {
                  setNewQuantity(parsed);
                }
              }}
              sx={{ width: 160 }}
              slotProps={{ htmlInput: { step: "any" } }}
            />
            <Button
              variant="outlined"
              onClick={handleAddRow}
              disabled={!newMaterialName || newQuantity === null}
            >
              {t("add")}
            </Button>
          </Stack>
        </DeliveryAdjustmentsToolbar>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("errorLoadingData")}
          </Alert>
        ) : null}
        <DeliveryAdjustmentsGrid
          rows={rows}
          onProcessRowUpdate={onProcessRowUpdate}
        />
      </Box>
    </AccountPageLayout>
  );
};
